import Link from "next/link";
import { Badge } from "./badge";

/*
 * Abweichung AW-017 (dauerhaft, Betreiber 09.10.2026, Wahl „A“): die Funktionskarte weicht vom Katalogblock
 * simple-icon-shortcut-card-grid-with-descriptions ab (Container, Abstände, Fokusring statt Schatten, Icon-Wrapper,
 * Pillen, Hervorhebung, „In Vorbereitung“ gestrichelt, Überschrift); Einzelheiten in docs/ui-preline/abweichungen.json.
 */

/**
 * Funktionskarte und Funktionskarten-Raster (Musterkatalog M-019, Design-Guide `funktions-karte.md`): ein Einstieg in
 * eine Funktion mit Symbol, Titel, Beschreibung und optionaler Zählpille. Das Raster trägt die Hub-Seite eines Bereichs
 * (Seitentyp T1): drei Spalten ab `lg`, zwei ab `sm`, eine mobil.
 *
 * Der Baustein kennt keine Fachregeln und prüft keine Rechte: WELCHE Karten es gibt, entscheidet der Aufrufer mit
 * derselben Prüffunktion wie das Ziel (r109/r110, Anbieten nie großzügiger als Erlauben); eine Karte ohne Recht wird
 * nicht übergeben (still weglassen, nie ausgegraut). Zustände:
 * - Normal: Link auf `href`, Hover mit Schatten, Fokusring.
 * - `hervorgehoben`: eigene Zeile über dem Raster, volle Breite; mit Link und Rand in Primärfarbe, Symbol im Kreis.
 * - `inVorbereitung`: gestrichelter Rand, KEIN Link (kein `aria-disabled`: ein
 *   div ohne Rolle darf es nicht tragen, der Zustand steht als Text in der Pille), Pille „In Vorbereitung“. Das ist keine
 *   Rechte-Sperre, sondern „die Funktion gibt es noch nicht“.
 * Die Zählpille trägt die Aussage im Text („2 offen“), nie nur in der Farbe; `aufmerksam` färbt sie gelb.
 */
export type FunktionsKarteZaehlerTon = "neutral" | "aufmerksam";

export interface FunktionsKarteDaten {
  /** Stabile Kennung (Schlüssel, Test-Anker). */
  id: string;
  titel: string;
  beschreibung: string;
  /** Lucide-Symbol des Aufrufers (R-F), wird als dekorativ markiert. */
  icon: React.ReactNode;
  /** Ziel der Karte; fehlt es (oder ist `inVorbereitung` gesetzt), ist die Karte kein Link. */
  href?: string;
  /** Zählpille neben dem Titel, z. B. „2 offen“. */
  zaehler?: { text: string; ton?: FunktionsKarteZaehlerTon };
  /** Zusatzpille für den Geltungsbereich, z. B. „Nur Betreiber“ (neutral). */
  zusatz?: string;
  /** Eigene Zeile über dem Raster, volle Breite. */
  hervorgehoben?: boolean;
  /** Funktion noch nicht gebaut: gestrichelter Rand, kein Link, Pille „In Vorbereitung“. */
  inVorbereitung?: boolean;
  /**
   * Fußzeile (additiv, Wiki P-W1, Betreiber 12.10.2026): ruhige Pille `marke` (z. B. „Unternehmen“) plus Satz `text`
   * (z. B. „48 Seiten · geändert vor 2 Std.“). Mit Fußzeile ist die Beschreibung auf zwei Zeilen gekürzt und die Fußzeile
   * sitzt am Kartenboden (gleich hohe Karten im Raster). Nur Sachangaben, nie Personen (r177/r179).
   */
  fuss?: { marke: string; text: string };
  /** KI-Karte (R-D, Mein Profil P3): Hülle `ai-gated`, als Link zusätzlich `ai-mark`; ohne `href` (KI aus) steht die Karte mit `ai-gated` und dem Fallback-Satz als Beschreibung. */
  ki?: boolean;
  /**
   * Ziel öffnet in einem neuen Fenster (additiv, Kantine „Tablet-Kiosk öffnen“, Betreiber 18.10.2026): `<a target="_blank"
   * rel="noopener noreferrer">` statt next/link, rechts das Lucide-Symbol `ExternalLink` (statt Chevron), Hinweis für
   * Sprachausgabe „(öffnet in neuem Fenster)“. Ohne `href` ohne Wirkung.
   */
  neuesFenster?: boolean;
}

// Feste, vollständige Klassenketten (nie zusammengesetzt).
// Ruhige Pille = Badge Ton „gedaempft“. Die gelbe bleibt eigen: Badge „warnung“ trägt dark:-Klassen (r254 verbietet sie
// hier), siehe Abweichung AW-017.
const PILLE_GELB = "py-0.5 px-2 inline-flex items-center rounded-full text-xs font-medium whitespace-nowrap bg-yellow-100 text-yellow-800";
const TITEL_AKTIV = "font-semibold text-foreground group-hover:text-primary-hover";
const TITEL_GEDAEMPFT = "font-semibold text-muted-foreground-1";

const HUELLE = "group flex flex-col rounded-xl shadow-2xs";
const LINK_NORMAL = `${HUELLE} bg-layer border border-layer-line hover:shadow-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus transition`;
const LINK_HERVOR = `${HUELLE} bg-layer border-2 border-primary hover:shadow-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus transition`;
const PLATZHALTER = "flex flex-col rounded-xl bg-layer border border-dashed border-layer-line text-muted-foreground-1";
// KI-Karten (R-D): Hülle `ai-gated`, als Link zusätzlich `ai-mark`
const PLATZHALTER_KI = `ai-gated ${PLATZHALTER}`;
const LINK_NORMAL_KI = `ai-gated ai-mark ${LINK_NORMAL}`;
const LINK_HERVOR_KI = `ai-gated ai-mark ${LINK_HERVOR}`;

function Koerper({ karte, kreis }: { karte: FunktionsKarteDaten; kreis: boolean }) {
  const vorbereitung = Boolean(karte.inVorbereitung);
  const symbolKlassen = kreis
    ? vorbereitung
      ? "shrink-0 inline-flex justify-center items-center size-11 rounded-full bg-muted text-muted-foreground-1"
      : "shrink-0 inline-flex justify-center items-center size-11 rounded-full bg-primary text-primary-foreground"
    : "mt-1 shrink-0 inline-flex size-5 text-foreground";
  const mitFuss = Boolean(karte.fuss) && !vorbereitung;
  return (
    <div className={mitFuss ? "p-4 md:p-5 grow flex flex-col" : "p-4 md:p-5"}>
      <div className="flex items-start gap-x-4 md:gap-x-5">
        <span className={symbolKlassen} aria-hidden="true">
          {karte.icon}
        </span>
        <div className="grow min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className={vorbereitung ? TITEL_GEDAEMPFT : TITEL_AKTIV}>{karte.titel}</h3>
            {vorbereitung && <Badge ton="gedaempft">In Vorbereitung</Badge>}
            {karte.zusatz && <Badge ton="gedaempft">{karte.zusatz}</Badge>}
            {karte.zaehler && !vorbereitung &&
              (karte.zaehler.ton === "aufmerksam" ? (
                <span className={PILLE_GELB}>{karte.zaehler.text}</span>
              ) : (
                <Badge ton="gedaempft">{karte.zaehler.text}</Badge>
              ))}
          </div>
          {karte.beschreibung && (
            <p className={mitFuss ? "mt-1 text-sm text-muted-foreground-1 line-clamp-2" : "mt-1 text-sm text-muted-foreground-1"}>{karte.beschreibung}</p>
          )}
        </div>
        {karte.neuesFenster && karte.href && !vorbereitung && (
          // Lucide `ExternalLink` als Inline-Pfad (das Paket bindet `lucide-react` nicht ein, wie `zeilen-symbole.tsx`).
          <svg
            className="shrink-0 self-center size-4 text-muted-foreground-1"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M15 3h6v6" />
            <path d="M10 14 21 3" />
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          </svg>
        )}
        {kreis && !vorbereitung && !(karte.neuesFenster && karte.href) && (
          <svg
            className="shrink-0 self-center size-4 text-muted-foreground-1"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        )}
      </div>
      {mitFuss && karte.fuss && (
        <div className="mt-auto pt-3 flex flex-wrap items-center gap-x-2 gap-y-1">
          <Badge ton="gedaempft">{karte.fuss.marke}</Badge>
          <span className="text-xs text-muted-foreground-1">{karte.fuss.text}</span>
        </div>
      )}
    </div>
  );
}

export function FunktionsKarte({ karte }: { karte: FunktionsKarteDaten }) {
  const kreis = Boolean(karte.hervorgehoben);
  const vorbereitung = Boolean(karte.inVorbereitung);
  if (vorbereitung || !karte.href) {
    return (
      <>
        {/* Preline: feedback-and-states/feedback-and-states-sub-actions-engagement/feedback-and-states-cat-action-cards-and-shortcut-links/simple-icon-shortcut-card-grid-with-descriptions */}
        <div className={karte.ki ? PLATZHALTER_KI : PLATZHALTER} data-funktionskarte={karte.id} data-ki={karte.ki ? "gated" : undefined}>
          <Koerper karte={karte} kreis={kreis} />
        </div>
      </>
    );
  }
  if (karte.neuesFenster) {
    return (
      <>
        {/* Preline: feedback-and-states/feedback-and-states-sub-actions-engagement/feedback-and-states-cat-action-cards-and-shortcut-links/simple-icon-shortcut-card-grid-with-descriptions */}
        <a
          className={karte.ki ? (kreis ? LINK_HERVOR_KI : LINK_NORMAL_KI) : kreis ? LINK_HERVOR : LINK_NORMAL}
          href={karte.href}
          target="_blank"
          rel="noopener noreferrer"
          data-funktionskarte={karte.id}
          data-ki={karte.ki ? "gated" : undefined}
        >
          <Koerper karte={karte} kreis={kreis} />
          <span className="sr-only">(öffnet in neuem Fenster)</span>
        </a>
      </>
    );
  }
  return (
    <>
      {/* Preline: feedback-and-states/feedback-and-states-sub-actions-engagement/feedback-and-states-cat-action-cards-and-shortcut-links/simple-icon-shortcut-card-grid-with-descriptions */}
      <Link className={karte.ki ? (kreis ? LINK_HERVOR_KI : LINK_NORMAL_KI) : kreis ? LINK_HERVOR : LINK_NORMAL} href={karte.href} data-funktionskarte={karte.id} data-ki={karte.ki ? "gated" : undefined}>
        <Koerper karte={karte} kreis={kreis} />
      </Link>
    </>
  );
}

/**
 * Raster der Funktionskarten. Hervorgehobene Karten stehen in eigener Zeile über dem Raster der übrigen; die optionale
 * Überschrift („Verwaltung“) trennt das Raster von den hervorgehobenen Karten und steht nur, wenn oben eine steht
 * (ohne hervorgehobene Karte gäbe sie nichts zu trennen).
 */
export function FunktionsKartenRaster({ karten, ueberschrift, ariaLabel }: { karten: FunktionsKarteDaten[]; ueberschrift?: string; ariaLabel?: string }) {
  const oben = karten.filter((k) => k.hervorgehoben);
  const raster = karten.filter((k) => !k.hervorgehoben);
  return (
    <div className="space-y-5" role="group" aria-label={ariaLabel}>
      {/* Gültige Überschriftenfolge (h1 der Seite, h2, Kartentitel h3): unsichtbare Überschrift der Gruppe */}
      <h2 className="sr-only">{ariaLabel ?? "Funktionen"}</h2>
      {oben.length > 0 && (
        <div className="space-y-3 sm:space-y-4">
          {oben.map((k) => (
            <FunktionsKarte key={k.id} karte={k} />
          ))}
        </div>
      )}
      {raster.length > 0 && (
        <div className="space-y-3">
          {ueberschrift && oben.length > 0 && (
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground-1">{ueberschrift}</h2>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {raster.map((k) => (
              <FunktionsKarte key={k.id} karte={k} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
